import { Eye, Monitor, Smartphone } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";

import type {
  OutgoingIntake,
  OutgoingIntakeFormNode
} from "@/api/types/outgoingIntake";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { PublicOutgoingIntake } from "@/pages/PublicOutgoingIntake/PublicOutgoingIntake";
import { cn } from "@/lib/utils";

import { usePreviewInitialData } from "./previewData";

type Device = "desktop" | "mobile";

const DEVICES: { value: Device; label: string; Icon: typeof Monitor }[] = [
  { value: "desktop", label: "Desktop", Icon: Monitor },
  { value: "mobile", label: "Phone", Icon: Smartphone }
];

/**
 * What the client sees: the existing client-facing intake, unchanged, in
 * preview mode inside a device-width frame, with a toolbar around it.
 * Anything shaped like an intake works: an open intake, or a saved template
 * in Firm Knowledge (whose questions are `schema`, greeting `clientName`).
 */
export const IntakePreview: FC<{
  intake: OutgoingIntake | null;
  schema?: OutgoingIntakeFormNode[];
  clientName?: string | null;
  /** After the device toggle (the dialog's Close). */
  actions?: ReactNode;
}> = ({ intake, schema, clientName, actions }) => {
  const intakeId = intake?.outgoing_intake_id ?? "";
  const [device, setDevice] = useState<Device>("desktop");
  const initialData = usePreviewInitialData(
    intake,
    schema ?? intake?.schema ?? [],
    null,
    clientName !== undefined ? { clientName } : undefined
  );

  if (!intake || !initialData) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-4" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line-1 bg-surface pr-2 pl-4">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="min-w-0 truncate text-base font-medium text-fg">
            {intake.title || "Intake"}
          </span>
          <StatusBadge>
            <Eye /> Preview
          </StatusBadge>
        </div>

        <ToggleGroup
          type="single"
          value={device}
          onValueChange={(v) => v && setDevice(v as Device)}
          className="shrink-0 gap-0.5 rounded-md bg-muted p-0.5"
          aria-label="Preview width"
        >
          {DEVICES.map(({ value, label, Icon }) => (
            <Tooltip key={value}>
              <TooltipTrigger asChild>
                {/* The tooltip trigger replaces `data-state`, so the selected
                    style keys off the radio item's `aria-checked`. */}
                <ToggleGroupItem
                  value={value}
                  aria-label={label}
                  className="h-6 w-7 min-w-0 rounded-sm px-0 text-muted-foreground hover:bg-transparent hover:text-foreground aria-checked:bg-background aria-checked:text-foreground aria-checked:shadow-sm"
                >
                  <Icon className="size-3.5" />
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent side="bottom">{label}</TooltipContent>
            </Tooltip>
          ))}
        </ToggleGroup>
        {actions}
      </div>
      <div className="flex min-h-0 flex-1 items-stretch justify-center bg-fill-1 p-4">
        <div
          className={cn(
            "relative min-h-0 w-full overflow-hidden rounded-lg border border-border bg-background",
            device === "mobile" ? "max-w-100" : "max-w-240"
          )}
        >
          <PublicOutgoingIntake
            key={`${intakeId}-${device}`}
            mode="preview"
            hideTopBar
            embeddedPreview
            previewViewport="responsive"
            tokenOverride="__preview__"
            initialData={initialData}
          />
        </div>
      </div>
    </div>
  );
};
